var _dec, _dec2, _dec3, _class, _NumberInput, _IconArrowOpenUpLine, _IconArrowOpenDownLin;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Fragment, Component } from 'react';
import keycode from 'keycode';
import { FormField } from '@instructure/ui-form-field';
import { IconArrowOpenDownLine, IconArrowOpenUpLine } from '@instructure/ui-icons';
import { testable } from '@instructure/ui-testable';
import { omitProps, pickProps, callRenderProp, getInteraction, withDeterministicId } from '@instructure/ui-react-utils';
import { hasVisibleChildren } from '@instructure/ui-a11y-utils';
import { withStyle } from '@instructure/emotion';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { allowedProps, propTypes } from './props';
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
---
category: components
id: NumberInput
---
**/
let NumberInput = (_dec = withDeterministicId(), _dec2 = withStyle(generateStyle, generateComponentTheme), _dec3 = testable(), _dec(_class = _dec2(_class = _dec3(_class = (_NumberInput = class NumberInput extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      hasFocus: false
    };
    this.ref = null;
    this._input = null;
    this._id = void 0;
    this.handleRef = el => {
      this.ref = el;
    };
    this.handleInputRef = element => {
      this._input = element;
      if (typeof this.props.inputRef === 'function') {
        this.props.inputRef(element);
      }
    };
    this.handleFocus = event => {
      this.setState({
        hasFocus: true
      });
      if (typeof this.props.onFocus === 'function') {
        this.props.onFocus(event);
      }
    };
    this.handleBlur = event => {
      this.setState({
        hasFocus: false
      });
      if (typeof this.props.onBlur === 'function') {
        this.props.onBlur(event);
      }
    };
    this.handleChange = event => {
      if (typeof this.props.onChange === 'function') {
        this.props.onChange(event, event.target.value);
      }
    };
    this.handleKeyDown = event => {
      const _this$props = this.props,
        onKeyDown = _this$props.onKeyDown,
        onDecrement = _this$props.onDecrement,
        onIncrement = _this$props.onIncrement;
      if (typeof onKeyDown === 'function') {
        onKeyDown(event);
      }
      if (event.keyCode === keycode.codes.down) {
        event.preventDefault();
        if (typeof onDecrement === 'function') {
          onDecrement(event);
        }
      } else if (event.keyCode === keycode.codes.up) {
        event.preventDefault();
        if (typeof onIncrement === 'function') {
          onIncrement(event);
        }
      }
    };
    this.handleClickUpArrow = event => {
      this.arrowClicked(event, this.props.onIncrement);
    };
    this.handleClickDownArrow = event => {
      this.arrowClicked(event, this.props.onDecrement);
    };
  }
  get id() {
    if (this.props.id) {
      return this.props.id;
    }
    if (!this._id) {
      this._id = this.props.deterministicId();
    }
    return this._id;
  }
  get invalid() {
    return !!this.props.messages && this.props.messages.some(message => message.type === 'error' || message.type === 'newError');
  }
  get interaction() {
    return getInteraction({
      props: this.props
    });
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props2;
    (_this$props$makeStyle = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props2, this.makeStyleVariables);
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props3;
    (_this$props$makeStyle2 = (_this$props3 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props3, this.makeStyleVariables);
  }
  get makeStyleVariables() {
    return {
      interaction: this.interaction,
      hasFocus: this.state.hasFocus,
      invalid: this.invalid
    };
  }
  arrowClicked(event, callback) {
    const interaction = this.interaction;
    event.preventDefault();
    if (interaction === 'enabled') {
      var _this$_input;
      (_this$_input = this._input) === null || _this$_input === void 0 ? void 0 : _this$_input.focus();
      if (typeof callback === 'function') {
        callback(event);
      }
    }
  }
  renderArrows(customIcons) {
    var _this$props$styles, _this$props$styles2, _this$props$styles3;
    return _jsxs("span", {
      css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.arrowContainer,
      children: [_jsx("button", {
        "aria-hidden": true,
        css: (_this$props$styles2 = this.props.styles) === null || _this$props$styles2 === void 0 ? void 0 : _this$props$styles2.arrow,
        onMouseDown: this.handleClickUpArrow,
        tabIndex: -1,
        type: "button",
        children: customIcons !== null && customIcons !== void 0 && customIcons.increase ? callRenderProp(customIcons.increase) : _IconArrowOpenUpLine || (_IconArrowOpenUpLine = _jsx(IconArrowOpenUpLine, {}))
      }), _jsx("button", {
        "aria-hidden": true,
        css: (_this$props$styles3 = this.props.styles) === null || _this$props$styles3 === void 0 ? void 0 : _this$props$styles3.arrow,
        onMouseDown: this.handleClickDownArrow,
        tabIndex: -1,
        type: "button",
        children: customIcons !== null && customIcons !== void 0 && customIcons.decrease ? callRenderProp(customIcons.decrease) : _IconArrowOpenDownLin || (_IconArrowOpenDownLin = _jsx(IconArrowOpenDownLine, {}))
      })]
    });
  }
  render() {
    var _this$props$styles4, _this$props$styles5, _this$props$styles6;
    const _this$props4 = this.props,
      renderLabel = _this$props4.renderLabel,
      display = _this$props4.display,
      placeholder = _this$props4.placeholder,
      isRequired = _this$props4.isRequired,
      showArrows = _this$props4.showArrows,
      value = _this$props4.value,
      width = _this$props4.width,
      styles = _this$props4.styles,
      allowStringValue = _this$props4.allowStringValue,
      renderIcons = _this$props4.renderIcons,
      margin = _this$props4.margin;
    const interaction = this.interaction;
    const rawLabel = callRenderProp(renderLabel);
    const label = hasVisibleChildren(rawLabel) ? _jsxs(Fragment, {
      children: [rawLabel, isRequired && _jsxs("span", {
        css: this.invalid ? styles === null || styles === void 0 ? void 0 : styles.requiredInvalid : {},
        "aria-hidden": true,
        children: [' ', "*"]
      })]
    }) : rawLabel;
    return _jsx(FormField, {
      ...pickProps(this.props, FormField.allowedProps),
      label: label,
      inline: display === 'inline-block',
      id: this.id,
      elementRef: this.handleRef,
      margin: margin,
      children: _jsx("span", {
        css: (_this$props$styles4 = this.props.styles) === null || _this$props$styles4 === void 0 ? void 0 : _this$props$styles4.inputWidth,
        style: width ? {
          width
        } : void 0,
        children: _jsxs("span", {
          css: (_this$props$styles5 = this.props.styles) === null || _this$props$styles5 === void 0 ? void 0 : _this$props$styles5.inputContainer,
          children: [_jsx("input", {
            ...omitProps(this.props, [...FormField.allowedProps, ...NumberInput.allowedProps]),
            css: (_this$props$styles6 = this.props.styles) === null || _this$props$styles6 === void 0 ? void 0 : _this$props$styles6.input,
            "aria-invalid": this.invalid ? 'true' : void 0,
            id: this.id,
            type: allowStringValue ? 'text' : 'number',
            inputMode: this.props.inputMode,
            placeholder: placeholder,
            ref: this.handleInputRef,
            required: isRequired,
            value: value,
            disabled: interaction === 'disabled',
            readOnly: interaction === 'readonly',
            onFocus: this.handleFocus,
            onBlur: this.handleBlur,
            onChange: this.handleChange,
            onKeyDown: this.handleKeyDown
          }), showArrows ? this.renderArrows(renderIcons) : null]
        })
      })
    });
  }
}, _NumberInput.displayName = "NumberInput", _NumberInput.componentId = 'NumberInput', _NumberInput.allowedProps = allowedProps, _NumberInput.propTypes = propTypes, _NumberInput.defaultProps = {
  // Leave interaction default undefined so that `disabled` and `readOnly` can also be supplied
  interaction: void 0,
  messages: [],
  isRequired: false,
  showArrows: true,
  size: 'medium',
  display: 'block',
  textAlign: 'start',
  inputMode: 'numeric',
  allowStringValue: false
}, _NumberInput)) || _class) || _class) || _class);
export default NumberInput;
export { NumberInput };